import {ButtonGroup, DataStatus, Form, FormButton, FormField, Separator} from 'platform/components';
import {Hide, Show, VStack} from 'platform/foundation';

import {useImperativeHandle, useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNotNilOrEmpty, toNumber} from 'ramda-adjunct';

import {useGetSalesFinanceSettingsQuery} from '@dms/api/sales';
import {CountryAlpha3CodeEnum} from '@dms/api/shared';
import {
  ListVehicleCostCategoriesApiResponse,
  ListVehicleEarningCategoriesApiResponse,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {CurrencyCodeType, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DocumentUploadForm} from './components/DocumentUploadForm';
import {FormVatPriceField} from './components/FormVatPriceField';
import {RecurringTransactionSubform} from './components/RecurringTransactionSubform';
import {
  CalculationTypeSchema,
  getVehicleTransactionFormSchema,
  TransactionType,
  TransactionTypeSchema,
  VatDirection,
  VehicleTransactionFormSchemaType,
} from './VehicleTransactionFormSchema';

type RealPriceType = NonNullable<VehicleTransactionFormSchemaType['realPrice']>;

export type VehicleTransactionFormRef = {
  formApi: UseFormReturn<VehicleTransactionFormSchemaType>;
};

type VehicleTransactionFormProps = RequiredTestIdProps & {
  ref?: React.Ref<VehicleTransactionFormRef>;
  onSubmit: (data: VehicleTransactionFormSchemaType) => Promise<void>;
  onDiscard?: VoidFunction;
  values?: Omit<VehicleTransactionFormSchemaType, 'realPrice'> & {
    // recurring prices does not include VAT info in response
    realPrice:
      | (Pick<RealPriceType, 'withVat' | 'withoutVat'> & {
          vatDirection?: VatDirection | undefined;
          vatType?: string | undefined;
          vatRate?: string | undefined;
        })
      | null;
  };
  currency: CurrencyCodeType;
  countryCode: CountryAlpha3CodeEnum;
  canSetExpectedPrice?: boolean;
  disableRecurring?: boolean;
  soldAt?: string | null;
  labels?: {
    categories?: string;
  };
} & (
    | {
        showCategories: true;
        categories: ListVehicleCostCategoriesApiResponse | ListVehicleEarningCategoriesApiResponse;
      }
    | {showCategories?: false; categories?: never}
  );

export function VehicleTransactionForm(props: VehicleTransactionFormProps) {
  const {ref, ...otherProps} = props;
  const isEditForm = isNotNilOrEmpty(otherProps.values);
  const defaultValues = isNotNilOrEmpty(otherProps.values) ? otherProps.values : undefined;
  const formApiRef = useRef<UseFormReturn<VehicleTransactionFormSchemaType>>(null);

  const {data: salesFinanceSettings, isLoading: isSalesFinanceSettingsLoading} =
    useGetSalesFinanceSettingsQuery();

  useImperativeHandle(ref, () => ({
    formApi: formApiRef.current as UseFormReturn<VehicleTransactionFormSchemaType>,
  }));

  return (
    <DataStatus isLoading={isSalesFinanceSettingsLoading}>
      <Form<VehicleTransactionFormSchemaType>
        onSubmit={(data) => {
          if (data.expectedPrice?.isCalculatingVat || data.realPrice?.isCalculatingVat) {
            return Promise.resolve();
          }
          return otherProps.onSubmit(data);
        }}
        mode="onBlur"
        defaultValues={{
          fileId: defaultValues?.fileId ?? null,
          category: null,
          startDate: null,
          endDate: null,
          calculationType: [CalculationTypeSchema.enum.SIMPLE],
          transactionType: [TransactionTypeSchema.enum.ONE_TIME],
          ...defaultValues,
          interestPA: isNaN(defaultValues?.interestPA ?? NaN)
            ? (toNumber(salesFinanceSettings?.defaultDailyInterestRate) ?? null)
            : defaultValues?.interestPA,
          expectedPrice: isNil(defaultValues?.expectedPrice?.withoutVat)
            ? null
            : defaultValues?.expectedPrice,
          realPrice: isNil(defaultValues?.realPrice?.withoutVat) ? null : defaultValues?.realPrice,
        }}
        onInvalidSubmit={(e) => {
          console.error('Invalid submit', e);
        }}
        experimentalZodSchema={getVehicleTransactionFormSchema(i18n)}
      >
        {(control, formApi) => {
          formApiRef.current = formApi;

          const isCalculatingVat =
            (formApi.watch('expectedPrice.isCalculatingVat') ||
              formApi.watch('realPrice.isCalculatingVat')) ??
            false;

          return (
            <>
              <VStack spacing={4}>
                <Show when={otherProps.showCategories && isNotNilOrEmpty(otherProps.categories)}>
                  <FormField
                    type="chips"
                    isDeselectable
                    name="category"
                    label={otherProps.labels?.categories ?? i18n.t('entity.vehicle.costs.category')}
                    control={control}
                    data-testid={suffixTestId('category', otherProps)}
                    options={
                      otherProps.categories?.map((category) => ({
                        value: category.key,
                        label: category.name,
                      })) ?? []
                    }
                  />
                </Show>
                <FormField
                  type="text"
                  control={control}
                  name="name"
                  isRequired
                  label={i18n.t('general.labels.name')}
                  data-testid={suffixTestId('name', otherProps)}
                />
                <FormVatPriceField
                  control={control}
                  name="expectedPrice"
                  data-testid="expectedPrice"
                  isDisabled={!otherProps.canSetExpectedPrice}
                  currency={otherProps.currency}
                  countryCode={otherProps.countryCode}
                  labels={{
                    withVat: i18n.t('entity.vehicle.costs.expectedPriceWithVAT'),
                    withoutVat: i18n.t('entity.vehicle.costs.expectedPriceWithoutVAT'),
                  }}
                  tooltips={
                    !otherProps.canSetExpectedPrice
                      ? {
                          withVat: i18n.t('entity.vehicle.costs.disabledExpectedPriceWithVAT'),
                          withoutVat: i18n.t(
                            'entity.vehicle.costs.disabledExpectedPriceWithoutVAT'
                          ),
                          vatType: i18n.t('entity.vehicle.costs.disabledExpectedPriceVATType'),
                        }
                      : undefined
                  }
                />
                <Hide when={otherProps.disableRecurring}>
                  <FormField
                    type="chips"
                    isDeselectable
                    name="transactionType"
                    control={control}
                    data-testid={suffixTestId('transactionType', otherProps)}
                    onChange={() => {
                      formApi.setValue('realPrice', null);
                    }}
                    options={
                      [
                        {
                          label: i18n.t('entity.vehicle.labels.transactionType.oneTime'),
                          value: TransactionTypeSchema.enum.ONE_TIME,
                        },
                        {
                          label: i18n.t('entity.vehicle.labels.transactionType.recurring'),
                          value: TransactionTypeSchema.enum.RECURRING,
                        },
                      ] satisfies {label: string; value: TransactionType}[]
                    }
                  />
                </Hide>
                <Show
                  when={
                    formApi.watch('transactionType')?.[0] === TransactionTypeSchema.enum.RECURRING
                  }
                >
                  <RecurringTransactionSubform
                    control={control}
                    currency={otherProps.currency}
                    soldAt={otherProps.soldAt ? parseDate(otherProps.soldAt) : null}
                  />
                </Show>
                <Show
                  when={
                    formApi.watch('transactionType')?.[0] === TransactionTypeSchema.enum.ONE_TIME
                  }
                >
                  <FormVatPriceField
                    control={control}
                    name="realPrice"
                    data-testid="realPrice"
                    currency={otherProps.currency}
                    countryCode={otherProps.countryCode}
                    labels={{
                      withVat: i18n.t('entity.vehicle.costs.realPriceWithVAT'),
                      withoutVat: i18n.t('entity.vehicle.costs.realPriceWithoutVAT'),
                    }}
                  />
                </Show>
                <FormField
                  minRows={1}
                  name="note"
                  type="textarea"
                  control={control}
                  label={i18n.t('general.labels.note')}
                  data-testid={suffixTestId('note', otherProps)}
                />
                <DocumentUploadForm
                  data-testid={otherProps['data-testid']}
                  name="fileId"
                  control={control}
                />
              </VStack>
              <Separator spacing={4} />
              <ButtonGroup align="right">
                <Show when={otherProps.onDiscard}>
                  <FormButton
                    variant="secondary"
                    title={i18n.t('general.actions.discard')}
                    onClick={otherProps.onDiscard}
                    control={control}
                    data-testid={suffixTestId('discard', otherProps)}
                  />
                </Show>
                <FormButton
                  type="submit"
                  variant="primary"
                  control={control}
                  isLoading={isCalculatingVat}
                  title={
                    isEditForm ? i18n.t('general.actions.save') : i18n.t('general.actions.add')
                  }
                  data-testid={suffixTestId('submit', otherProps)}
                />
              </ButtonGroup>
            </>
          );
        }}
      </Form>
    </DataStatus>
  );
}
